
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

const sections = [
  {
    title: 'Agreement to Terms',
    content: [
      'By accessing or using Atlas System\'s services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.',
    ],
  },
  {
    title: 'Services',
    content: [
      'Atlas System provides website development, marketing automation, and related digital marketing services for contractors and local service businesses. Our services include but are not limited to:',
      'Website design and development',
      'Missed call text-back automation',
      'Review management systems',
      'Lead follow-up automation',
      'Local SEO optimization',
      'Paid advertising management (Growth plan)',
    ],
  },
  {
    title: 'Payment Terms',
    content: [
      'Services are billed monthly in advance. Payment is due on the date specified in your invoice. Failure to pay may result in suspension or termination of services.',
    ],
  },
  {
    title: 'Cancellation',
    content: [
      'You may cancel your subscription at any time with 30 days written notice. Upon cancellation, you will retain access to your services until the end of your current billing period.',
    ],
  },
  {
    title: 'SMS/Text Messaging Terms',
    content: [
      'By opting in to receive SMS/text messages from Atlas System, you agree to the following terms:',
    ],
    subsections: [
      {
        subtitle: 'Use Cases',
        items: [
          'Atlas System may send you text messages for the following purposes:',
          'Marketing promotions and special offers',
          'Service announcements and updates',
          'Appointment reminders and confirmations',
          'Follow-up communications regarding your inquiries',
          'Tips and educational content related to our services',
        ],
      },
      {
        subtitle: 'Opt-Out Instructions',
        items: [
          'You can opt out of receiving text messages at any time by replying STOP to any message you receive from us. After opting out, you will receive a one-time confirmation message, and no further messages will be sent unless you opt in again. You may also opt out by contacting us at hello@atlassystem.com. For help, reply HELP to any message.',
        ],
      },
      {
        subtitle: 'Message & Data Rates',
        items: [
          'Message and data rates may apply to any text messages sent or received. Message frequency varies based on your interactions with us. Please contact your mobile carrier for details about your text messaging plan.',
        ],
      },
      {
        subtitle: 'Carrier Liability Disclaimer',
        items: [
          'Atlas System is not responsible for any delays or failures in the delivery of text messages. Carriers are not liable for delayed or undelivered messages. Mobile carriers and their service providers are not responsible for the content of any messages sent through our SMS program. Atlas System disclaims all liability for any damages arising from the transmission of text messages.',
        ],
      },
      {
        subtitle: 'Age Restriction',
        items: [
          'You must be at least 18 years of age to opt in to our SMS program. By providing your phone number and consenting to receive text messages, you confirm that you are 18 years of age or older.',
        ],
      },
    ],
  },
  {
    title: 'Intellectual Property',
    content: [
      'Upon full payment, you own the website content and design created specifically for your business. Atlas System retains ownership of proprietary systems, code frameworks, and automation templates.',
    ],
  },
  {
    title: 'Client Responsibilities',
    content: [
      'You agree to provide accurate information, respond to requests in a timely manner, and ensure that content you provide does not violate any laws or third-party rights.',
    ],
  },
  {
    title: 'Limitation of Liability',
    content: [
      'Atlas System shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services. Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.',
    ],
  },
  {
    title: 'Modifications',
    content: [
      'We reserve the right to modify these terms at any time. We will notify you of any material changes via email or through our services.',
    ],
  },
  {
    title: 'Contact',
    content: [
      'For questions about these Terms of Service, please contact us at info@atlassystem.io.',
    ],
  },
];

export default function TermsPage() {
  const [scrolled, setScrolled] = useState(false);

  // Track scroll position to toggle nav background/shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure the page always starts at the top when mounted
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-300
          ${scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}
        `}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/e31f2f4702878072217cd119276d8644/5b56961b60c36d4ff2ff86c82d13a1e4.png"
              alt="AI Receptionist Logo"
              className="h-12 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Contact
            </Link>
            <a
              href="#contact"
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </a>
          </div>
        </div>
      </nav>

      {/* Content */}
      <article className="pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-widest text-purple-600 uppercase mb-4">
            LEGAL
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-sm text-gray-500 mb-12">
            Last updated: February 18, 2026
          </p>

          <div className="prose prose-gray max-w-none">
            {sections.map((section, i) => (
              <div key={i} className="mb-10">
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  <a
                    href={`#term-${i}`}
                    id={`term-${i}`}
                    className="hover:text-purple-600 transition-colors"
                  >
                    {i + 1}. {section.title}
                  </a>
                </h4>
                <ul className="space-y-3">
                  {section.content.map((item, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2 flex-shrink-0"></span>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {item}
                      </p>
                    </li>
                  ))}
                </ul>
                
                {section.subsections && (
                  <div className="mt-6 ml-6 space-y-6">
                    {section.subsections.map((subsection, k) => (
                      <div key={k}>
                        <h5 className="text-base font-semibold text-gray-800 mb-3">
                          {subsection.subtitle}
                        </h5>
                        <ul className="space-y-2">
                          {subsection.items.map((item, l) => (
                            <li key={l} className="flex items-start gap-3">
                              <span className="w-1 h-1 bg-purple-300 rounded-full mt-2 flex-shrink-0"></span>
                              <p className="text-sm text-gray-600 leading-relaxed">
                                {item}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
